import React, { useState } from 'react';
import {
  Box, Drawer, List, ListItem, ListItemIcon, ListItemText,
  Typography, IconButton, Avatar, Collapse, Divider,
  Menu, MenuItem, Dialog, DialogContent, Tooltip
} from '@mui/material';
import {
  Dashboard, People, CollectionsBookmark, Restaurant,
  AccountBalance, RemoveCircle, Receipt, BarChart,
  EmojiEvents, LocalDrink, Assessment, Settings,
  ExpandLess, ExpandMore, Menu as MenuIcon, Logout,
  PersonOutline, ChevronRight, Palette, Check, Sms, PrecisionManufacturing
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { logout } from '../api/authApi';
import { getRefreshToken } from '../utils/tokenHelper';
import { useApp, THEMES, LANGS } from '../context/AppContext';
import { hasAnyModulePermission } from '../utils/permissions';

const DRAWER_WIDTH = 245;

export default function Layout({ children, title, hideNavigation = false }) {
  const navigate             = useNavigate();
  const location             = useLocation();
  const { user, logoutUser } = useAuth();
  const { theme, themeKey, setThemeKey, lang, setLang, t } = useApp();

  const [mobileOpen,     setMobileOpen]     = useState(false);
  const [expanded,       setExpanded]       = useState({});
  const [anchorEl,       setAnchorEl]       = useState(null);
  const [showAppearance, setShowAppearance] = useState(false);

  const NAV_ITEMS = [
    { tKey:'dashboard',   icon:<Dashboard/>,           path:'/dashboard'    },
    { tKey:'collections', icon:<CollectionsBookmark/>, path:'/collections'  },
    { tKey:'automaticCollection', icon:<PrecisionManufacturing/>, path:'/automatic-collection' },
    { tKey:'farmers',     icon:<People/>,              path:'/farmers'      },
    { tKey:'foods',       icon:<Restaurant/>,          path:'/foods'        },
    { tKey:'advance',     icon:<AccountBalance/>,      path:'/advance'      },
    { tKey:'deduction',   icon:<RemoveCircle/>,        path:'/deduction'    },
    { tKey:'invoices',    icon:<Receipt/>,             path:'/invoices'     },
    { tKey:'rateChart',   icon:<BarChart/>,            path:'/rate-chart'   },
    { tKey:'annualBonus', icon:<EmojiEvents/>,         path:'/annual-bonus' },
    { tKey:'milkSale',    icon:<LocalDrink/>, children:[
        { tKey:'milkSale',      path:'/milk-sale'       },
        { tKey:'customers',     path:'/customers'       },
        { tKey:'customerKhata', path:'/customer-khata'  },
        { tKey:'customerBill',  path:'/customer-bill'   },
        { tKey:'rateChart',     path:'/milk-rate-chart' },
    ]},
    { tKey:'reports',  icon:<Assessment/>, path:'/reports' },
    { tKey:'smsLogs',  icon:<Sms/>,       path:'/sms-logs' },
    { tKey:'settings', icon:<Settings/>,  children:[
        { tKey:'centerInfo', path:'/center-info' },
    ]},
  ];
  const isOperator = String(user?.roleCode || '').toUpperCase() === 'OPERATOR';
  const visibleNavItems = isOperator ? NAV_ITEMS.filter(item => {
    const moduleByPath = {
      '/dashboard':'Dashboard', '/collections':'Collection', '/automatic-collection':'Collection',
      '/farmers':'Farmers', '/foods':'Feed', '/advance':'Advance', '/deduction':'Deduction',
      '/invoices':'Invoices', '/rate-chart':'RateChart', '/annual-bonus':'AnnualBonus',
      '/reports':'Reports', '/sms-logs':'SmsLogs'
    };
    if (item.children?.some(child => child.path === '/center-info')) return hasAnyModulePermission(user, 'Settings');
    if (item.children?.some(child => child.path === '/milk-sale')) return hasAnyModulePermission(user, 'MilkSale');
    return hasAnyModulePermission(user, moduleByPath[item.path]);
  }) : NAV_ITEMS;

  const isActive     = (path) => location.pathname === path;
  const getLabel     = (item) => t(item.tKey);
  const handleExpand = (key)  => setExpanded(p => ({ ...p, [key]: !p[key] }));
  const grad         = `linear-gradient(135deg,${theme.primary},${theme.hover})`;

  const handleLogout = async () => {
    try { const tok = getRefreshToken(); if (tok) await logout({ refreshToken: tok }); } catch {}
    logoutUser(); navigate('/login');
  };

  const drawerContent = (
    <Box sx={{ height:'100%', display:'flex', flexDirection:'column', overflow:'hidden' }}>
      <Box sx={{ p:2, background:grad, display:'flex', alignItems:'center', gap:1, flexShrink:0 }}>
        <Typography variant="h6" sx={{ color:'#fff', fontWeight:800, fontFamily:'Poppins,sans-serif', letterSpacing:'-0.3px' }}>
          🐄 MicroDairy
        </Typography>
      </Box>
      <Box sx={{ px:2, py:1.2, background:theme.light, borderBottom:`1px solid ${theme.border}`, flexShrink:0 }}>
        <Typography sx={{ color:theme.primary, fontWeight:700, fontSize:'0.7rem', letterSpacing:'0.3px', wordBreak:'break-word' }}>
          [{user?.centerCode}] {user?.centerName?.toUpperCase()}
        </Typography>
      </Box>
      <List sx={{ flex:1, py:0.5, overflowY:'auto', overflowX:'hidden' }}>
        {visibleNavItems.map(item => {
          const lbl = getLabel(item); const active = isActive(item.path);
          return (
            <React.Fragment key={item.tKey}>
              <ListItem component="div"
                onClick={() => { if (item.children) handleExpand(item.tKey); else { navigate(item.path); setMobileOpen(false); } }}
                sx={{ mx:0.8, borderRadius:1.5, mb:0.2, py:0.7,
                  background:active?grad:'transparent', color:active?'#fff':'#374151',
                  '&:hover':{ background:active?grad:theme.light } }}>
                <ListItemIcon sx={{ minWidth:34, color:active?'#fff':theme.primary }}>
                  {React.cloneElement(item.icon, { sx:{ fontSize:20 } })}
                </ListItemIcon>
                <ListItemText primary={lbl} primaryTypographyProps={{ fontSize:'0.85rem', fontWeight:active?700:500 }}/>
                {item.children && (expanded[item.tKey]
                  ? <ExpandLess sx={{ fontSize:16, color:active?'#fff':'#9ca3af' }}/>
                  : <ExpandMore  sx={{ fontSize:16, color:active?'#fff':'#9ca3af' }}/>)}
              </ListItem>
              {item.children && (
                <Collapse in={!!expanded[item.tKey]} timeout="auto" unmountOnExit>
                  <List disablePadding>
                    {item.children.map(child => (
                      <ListItem component="div" key={child.tKey} onClick={() => { navigate(child.path); setMobileOpen(false); }}
                        sx={{ pl:4.5, mx:0.8, borderRadius:1.5, mb:0.15, py:0.55,
                          background:isActive(child.path)?theme.light:'transparent',
                          '&:hover':{ background:theme.light } }}>
                        <ListItemIcon sx={{ minWidth:20 }}>
                          <ChevronRight sx={{ fontSize:13, color:isActive(child.path)?theme.primary:'#9ca3af' }}/>
                        </ListItemIcon>
                        <ListItemText primary={t(child.tKey)}
                          primaryTypographyProps={{ fontSize:'0.8rem', fontWeight:isActive(child.path)?700:400,
                            color:isActive(child.path)?theme.primary:'#6b7280' }}/>
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          );
        })}
      </List>
      <Divider/>
      <Box sx={{ p:1.5, background:'#fafafa', flexShrink:0 }}>
        <Typography variant="caption" sx={{ color:'#9ca3af', display:'block', textAlign:'center', fontSize:'0.7rem' }}>MicroDairy © 2026</Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display:'flex', minHeight:'100vh', background:'#f3f4f6' }}>
      {!hideNavigation && <Drawer variant="permanent"
        sx={{ width:DRAWER_WIDTH, flexShrink:0, display:{ xs:'none', md:'block' },
          '& .MuiDrawer-paper':{ width:DRAWER_WIDTH, boxSizing:'border-box', border:'none', boxShadow:'2px 0 8px rgba(0,0,0,0.07)', overflowX:'hidden' } }}>
        {drawerContent}
      </Drawer>}
      {!hideNavigation && <Drawer variant="temporary" open={mobileOpen} onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted:true }}
        sx={{ display:{ xs:'block', md:'none' }, '& .MuiDrawer-paper':{ width:DRAWER_WIDTH, overflowX:'hidden' } }}>
        {drawerContent}
      </Drawer>}
      <Box sx={{ flex:1, display:'flex', flexDirection:'column', minHeight:'100vh', minWidth:0 }}>
        <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between',
          px:{ xs:2, md:3 }, py:1.2, background:'#fff', borderBottom:'1px solid #e5e7eb',
          boxShadow:'0 1px 3px rgba(0,0,0,0.05)', position:'sticky', top:0, zIndex:1100, flexShrink:0 }}>
          {!hideNavigation && <IconButton size="small" sx={{ display:{ md:'none' }, mr:1 }} onClick={() => setMobileOpen(true)}><MenuIcon/></IconButton>}
          <Typography sx={{ fontWeight:700, color:theme.primary, fontSize:'0.82rem', display:{ xs:'none', md:'block' } }}>
            [{user?.centerCode}] {user?.centerName?.toUpperCase()}
          </Typography>
          <Typography sx={{ fontWeight:700, color:'#111827', fontSize:'1rem', display:{ xs:'block', md:'none' } }}>{title}</Typography>
          <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
            <Tooltip title={t('appearance')}>
              <IconButton size="small" onClick={() => setShowAppearance(true)}
                sx={{ border:`1.5px solid ${theme.border}`, borderRadius:1.5, p:0.6, color:theme.primary, '&:hover':{ background:theme.light } }}>
                <Palette sx={{ fontSize:18 }}/>
              </IconButton>
            </Tooltip>
            <Avatar onClick={(e) => setAnchorEl(e.currentTarget)}
              sx={{ width:34, height:34, background:grad, cursor:'pointer', fontWeight:700, fontSize:'0.85rem' }}>
              {user?.fullName?.charAt(0)?.toUpperCase() || 'A'}
            </Avatar>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}
              transformOrigin={{ horizontal:'right', vertical:'top' }} anchorOrigin={{ horizontal:'right', vertical:'bottom' }}
              PaperProps={{ elevation:3, sx:{ borderRadius:2, minWidth:220, mt:0.5, overflow:'hidden', boxShadow:'0 4px 20px rgba(0,0,0,0.12)' } }}>
              <Box sx={{ px:2, py:1.8, background:grad, display:'flex', gap:1.5, alignItems:'center' }}>
                <Avatar sx={{ background:'rgba(255,255,255,0.25)', fontWeight:700, width:38, height:38 }}>
                  {user?.fullName?.charAt(0)?.toUpperCase() || 'A'}
                </Avatar>
                <Box sx={{ minWidth:0 }}>
                  <Typography variant="body2" sx={{ color:'#fff', fontWeight:700, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{user?.fullName}</Typography>
                  <Typography variant="caption" sx={{ color:'rgba(255,255,255,0.8)' }}>{user?.mobileNumber}</Typography>
                </Box>
              </Box>
              <Divider/>
              <MenuItem onClick={() => setAnchorEl(null)} sx={{ py:1.2, '&:hover':{ background:theme.light } }}>
                <PersonOutline sx={{ mr:1.5, color:theme.primary, fontSize:18 }}/>
                <Typography variant="body2" sx={{ fontWeight:500 }}>{t('profile')}</Typography>
              </MenuItem>
              <MenuItem onClick={() => { setAnchorEl(null); setShowAppearance(true); }} sx={{ py:1.2, '&:hover':{ background:theme.light } }}>
                <Palette sx={{ mr:1.5, color:theme.primary, fontSize:18 }}/>
                <Typography variant="body2" sx={{ fontWeight:500 }}>{t('appearance')}</Typography>
              </MenuItem>
              <MenuItem onClick={() => { setAnchorEl(null); handleLogout(); }} sx={{ py:1.2, '&:hover':{ background:'#fff5f5' } }}>
                <Logout sx={{ mr:1.5, color:'#dc2626', fontSize:18 }}/>
                <Typography variant="body2" sx={{ color:'#dc2626', fontWeight:500 }}>{t('logout')}</Typography>
              </MenuItem>
            </Menu>
          </Box>
        </Box>
        <Box sx={{ flex:1, p:hideNavigation?{ xs:1, md:1.5 }:{ xs:1.5, md:2.5 }, minWidth:0 }}>{children}</Box>
      </Box>

      <Dialog open={showAppearance} onClose={() => setShowAppearance(false)}
        maxWidth="xs" fullWidth PaperProps={{ sx:{ borderRadius:3, overflow:'hidden' } }}>
        <Box sx={{ px:2.5, py:1.8, background:grad, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
            <Palette sx={{ color:'#fff', fontSize:20 }}/>
            <Typography sx={{ color:'#fff', fontWeight:700, fontSize:'1rem' }}>{t('appearance')}</Typography>
          </Box>
          <IconButton size="small" onClick={() => setShowAppearance(false)} sx={{ color:'rgba(255,255,255,0.8)', p:0.3 }}>✕</IconButton>
        </Box>
        <DialogContent sx={{ px:3, py:2.5 }}>
          <Typography variant="body2" sx={{ fontWeight:700, color:'#555', mb:1.5, fontSize:'0.85rem' }}>🌐 {t('language')}</Typography>
          <Box sx={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:1, mb:3 }}>
            {Object.entries(LANGS).map(([code, info]) => {
              const sel = lang === code;
              return (
                <Box key={code} onClick={() => setLang(code)}
                  sx={{ p:1.2, borderRadius:2, border:`2px solid ${sel?theme.primary:'#e5e7eb'}`,
                    background:sel?theme.light:'#fafafa', cursor:'pointer',
                    display:'flex', alignItems:'center', justifyContent:'space-between',
                    transition:'all 0.15s', '&:hover':{ borderColor:theme.primary, background:theme.light } }}>
                  <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
                    <Typography sx={{ fontSize:'1.3rem', lineHeight:1 }}>{info.flag}</Typography>
                    <Box>
                      <Typography sx={{ fontSize:'0.82rem', fontWeight:700, color:sel?theme.primary:'#333', lineHeight:1.2 }}>{info.native}</Typography>
                      <Typography sx={{ fontSize:'0.65rem', color:'#888' }}>{info.name}</Typography>
                    </Box>
                  </Box>
                  {sel && <Check sx={{ fontSize:16, color:theme.primary }}/>}
                </Box>
              );
            })}
          </Box>
          <Typography variant="body2" sx={{ fontWeight:700, color:'#555', mb:1.5, fontSize:'0.85rem' }}>🎨 {t('themeColor')}</Typography>
          <Box sx={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:1.2, mb:0.5 }}>
            {Object.entries(THEMES).map(([key, th]) => {
              const sel = themeKey === key;
              return (
                <Tooltip key={key} title={th.name}>
                  <Box onClick={() => setThemeKey(key)}
                    sx={{ position:'relative', height:46, borderRadius:2.5,
                      background:`linear-gradient(135deg,${th.primary},${th.hover})`,
                      cursor:'pointer',
                      boxShadow:sel?`0 0 0 3px #fff, 0 0 0 5px ${th.primary}`:'0 2px 6px rgba(0,0,0,0.18)',
                      transition:'all 0.15s', '&:hover':{ transform:'scale(1.1)' } }}>
                    {sel && <Check sx={{ position:'absolute', top:'50%', left:'50%', transform:'translate(-50%,-50%)', color:'#fff', fontSize:20 }}/>}
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
          <Box sx={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:1.2 }}>
            {Object.entries(THEMES).map(([key, th]) => (
              <Typography key={key} variant="caption"
                sx={{ textAlign:'center', fontSize:'0.62rem', color:themeKey===key?theme.primary:'#999', fontWeight:themeKey===key?700:400 }}>
                {th.name}
              </Typography>
            ))}
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
